import styled from 'styled-components';
import { ButtonProps, GhostButton, TertiaryButton } from '@ardoq/button';
import { radius, shadowM, space } from '@ardoq/design-tokens';
import { CloseIcon, Icon, IconName, IconSize } from '@ardoq/icons';
import { ToastType, ToastPosition, Toast as ToastInterface } from './types';
import { getAnimationCSS, getKeyframes } from './animationUtils';
import { perTypeColorMap } from '../const';
import { StatusType } from '../types';

const ToastWrapper = styled.div<{
  $index: number;
  $shouldMoveUp: boolean;
  $animate?: boolean;
  $position: ToastPosition;
  $maxWidth: 'fit-content' | undefined;
}>`
  width: fit-content;
  box-shadow: ${shadowM};
  position: relative;
  border-radius: ${radius.r4};
  pointer-events: auto; /* So that children of ToastsContainer can be clicked */
  ${({ $index, $shouldMoveUp, $animate, $position }) =>
    getAnimationCSS({
      index: $index,
      shouldMoveUp: $shouldMoveUp,
      animate: $animate,
      position: $position,
    })};
  ${getKeyframes()}

  ${({ $maxWidth }) =>
    $maxWidth === 'fit-content'
      ? 'max-width: fit-content;'
      : 'max-width: 400px;'}
`;

const CloseButton = (props: ButtonProps) => {
  return (
    <GhostButton {...props}>
      <CloseIcon />
    </GhostButton>
  );
};

const NoWrapTertiaryButton = styled(TertiaryButton)`
  white-space: nowrap;
`;

const ToastBody = styled.div<{
  $backgroundColor: string;
  $borderColor: string;
  $textColor: string;
}>`
  display: flex;
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  color: ${({ $textColor }) => $textColor};
  padding: ${space.medium};
  border-radius: ${radius.r4};
  gap: ${space.medium};
`;

type ToastProps = { animate?: boolean } & ToastInterface;

const toastTypeToStatusTypeMap = {
  [ToastType.INFO]: StatusType.INFO,
  [ToastType.SUCCESS]: StatusType.SUCCESS,
  [ToastType.ERROR]: StatusType.ERROR,
};

const iconMap = {
  [ToastType.INFO]: IconName.INFO,
  [ToastType.SUCCESS]: IconName.INFO,
  [ToastType.ERROR]: IconName.ERROR,
};

export const Toast = ({
  message,
  removeToast,
  index,
  shouldMoveUp,
  animate,
  type,
  position,
  timeout,
  actionButton,
  maxWidth,
}: ToastProps) => {
  const colorMap = perTypeColorMap[toastTypeToStatusTypeMap[type]];
  return (
    <ToastWrapper
      $index={index}
      $shouldMoveUp={shouldMoveUp}
      $animate={animate !== false && timeout !== false}
      $position={position}
      $maxWidth={maxWidth}
      data-test-id="toast-notification"
      role="alert"
      className="ardoq-toast-notification"
    >
      <ToastBody
        $backgroundColor={colorMap.backgroundColor}
        $borderColor={colorMap.borderColor}
        $textColor={colorMap.labelColor}
      >
        <Icon
          iconName={iconMap[type]}
          iconSize={IconSize.MEDIUM}
          color={colorMap.iconColor}
        />
        {message}
        {actionButton && (
          <NoWrapTertiaryButton
            onClick={() => {
              actionButton.onClick();
              removeToast();
            }}
          >
            {actionButton.label}
          </NoWrapTertiaryButton>
        )}
        <CloseButton onClick={removeToast} />
      </ToastBody>
    </ToastWrapper>
  );
};
